import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, Eye, EyeOff, KeyRound, Mail, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import PasswordRequirementList from "../components/portal/PasswordRequirementList";
import { usePortalPasswordReset } from "../hooks/usePortalPasswordReset";
import { isStrongPassword } from "../lib/passwordPolicy";


const inputClass = "rounded-none border border-zinc-300 bg-white px-3 py-2.5 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] focus:outline-none w-full";


export default function PortalForgot() {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const {
    stage,
    maskedEmail,
    otpPreview,
    countdown,
    loading,
    error,
    setError,
    requestOtp,
    resendOtp,
    verifyOtp,
    completeReset,
  } = usePortalPasswordReset();

  const submitEmail = async (event) => {
    event.preventDefault();
    if (await requestOtp(email)) toast.success("Kode OTP sudah dikirim ke email Anda.");
  };

  const submitOtp = async (event) => {
    event.preventDefault();
    if (await verifyOtp(otp)) toast.success("Kode OTP berhasil diverifikasi.");
  };

  const handleResend = async () => {
    setOtp("");
    if (await resendOtp()) toast.success("Kode OTP baru sudah dikirim.");
  };

  const submitPassword = async (event) => {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("Konfirmasi password tidak sama dengan password baru.");
      return;
    }
    if (!isStrongPassword(newPassword)) {
      setError("Password belum memenuhi seluruh ketentuan keamanan.");
      return;
    }
    if (await completeReset(newPassword, confirmPassword)) {
      window.location.replace("/portal/login?password-reset=success");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4 sm:p-6">
      <main className="w-full max-w-md rounded-xl border border-zinc-200 bg-white p-6 shadow-sm sm:p-8">
        <Link to="/portal/login" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-900 font-semibold">
          <ChevronLeft className="w-3.5 h-3.5" /> Kembali ke Login
        </Link>

        {stage === "email" && (
          <>
            <div className="mt-6">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Portal Karyawan</div>
              <h2 className="font-heading text-3xl font-bold tracking-tight text-zinc-900 mt-2">Lupa Password</h2>
              <p className="text-sm text-zinc-500 mt-2">Masukkan email yang terdaftar. Kami akan mengirim kode OTP 4 digit.</p>
            </div>
            <form onSubmit={submitEmail} className="mt-8 space-y-5">
              <div>
                <label className="block text-xs font-semibold text-zinc-900 uppercase tracking-wider mb-1.5">Email Terdaftar</label>
                <input
                  data-testid="forgot-password-email-input"
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className={inputClass}
                  placeholder="anda@perusahaan.com"
                />
              </div>
              <button
                data-testid="forgot-password-submit-button"
                type="submit"
                disabled={loading}
                className="rounded-none w-full bg-[#002FA7] text-white px-5 py-3 font-semibold hover:bg-[#002FA7]/90 inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Mail className="w-4 h-4" /> {loading ? "Mengirim…" : "Kirim Kode OTP"} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </>
        )}

        {stage === "otp" && (
          <>
            <div className="mt-6">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Verifikasi Email</div>
              <h2 className="font-heading text-3xl font-bold tracking-tight text-zinc-900 mt-2">Masukkan Kode OTP</h2>
              <p className="text-sm text-zinc-500 mt-2">Kode dikirim ke <strong className="text-zinc-700">{maskedEmail}</strong> dan berlaku selama 1 menit.</p>
            </div>
            <form onSubmit={submitOtp} className="mt-8 space-y-5">
              <div>
                <label className="block text-xs font-semibold text-zinc-900 uppercase tracking-wider mb-1.5">Kode OTP</label>
                <input
                  data-testid="password-reset-otp-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  autoFocus
                  maxLength={4}
                  value={otp}
                  onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 4))}
                  className={`${inputClass} text-center font-mono text-2xl font-bold tracking-[0.5em]`}
                  placeholder="0000"
                />
              </div>
              <button
                data-testid="password-reset-verify-button"
                type="submit"
                disabled={loading || otp.length !== 4 || countdown === 0}
                className="rounded-none w-full bg-[#002FA7] text-white px-5 py-3 font-semibold hover:bg-[#002FA7]/90 inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <ShieldCheck className="w-4 h-4" /> {loading ? "Memverifikasi…" : "Verifikasi OTP"}
              </button>
            </form>

            <div className="mt-4 text-center text-xs text-zinc-500">
              {countdown > 0 ? `Kode berlaku selama ${countdown} detik.` : "Kode OTP sudah kedaluwarsa."}
            </div>
            <button
              data-testid="password-reset-resend-button"
              type="button"
              onClick={handleResend}
              disabled={loading || countdown > 0}
              className="mt-3 w-full border border-zinc-300 px-4 py-2.5 text-sm font-semibold text-[#002FA7] hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-40 inline-flex items-center justify-center gap-2"
            >
              <RefreshCw className="h-4 w-4" /> Kirim Ulang Kode OTP
            </button>

            {otpPreview && (
              <div className="mt-4 border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
                Mode pengembangan: kode OTP <strong className="font-mono text-base">{otpPreview}</strong>. Konfigurasikan `RESEND_API_KEY` untuk pengiriman email.
              </div>
            )}
          </>
        )}

        {stage === "password" && (
          <>
            <div className="mt-6">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Langkah Terakhir</div>
              <h2 className="font-heading text-3xl font-bold tracking-tight text-zinc-900 mt-2">Buat Password Baru</h2>
              <p className="text-sm text-zinc-500 mt-2">Gunakan password kuat yang belum pernah dibagikan kepada orang lain.</p>
            </div>
            <form onSubmit={submitPassword} className="mt-8 space-y-5">
              <div>
                <label className="block text-xs font-semibold text-zinc-900 uppercase tracking-wider mb-1.5">Password Baru</label>
                <div className="relative">
                  <input
                    data-testid="new-password-input"
                    type={showNewPassword ? "text" : "password"}
                    required
                    autoFocus
                    maxLength={72}
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    className={`${inputClass} pr-11`}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    data-testid="toggle-new-password-visibility"
                    onClick={() => setShowNewPassword((visible) => !visible)}
                    aria-label={showNewPassword ? "Sembunyikan password baru" : "Tampilkan password baru"}
                    aria-pressed={showNewPassword}
                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-zinc-500 hover:text-[#002FA7] focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <PasswordRequirementList password={newPassword} />
              <div>
                <label className="block text-xs font-semibold text-zinc-900 uppercase tracking-wider mb-1.5">Konfirmasi Password</label>
                <div className="relative">
                  <input
                    data-testid="confirm-password-input"
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    maxLength={72}
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    className={`${inputClass} pr-11`}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    data-testid="toggle-confirm-password-visibility"
                    onClick={() => setShowConfirmPassword((visible) => !visible)}
                    aria-label={showConfirmPassword ? "Sembunyikan konfirmasi password" : "Tampilkan konfirmasi password"}
                    aria-pressed={showConfirmPassword}
                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-zinc-500 hover:text-[#002FA7] focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {confirmPassword && newPassword !== confirmPassword && (
                  <div className="mt-1.5 text-xs text-red-600">Konfirmasi password belum sama.</div>
                )}
              </div>
              <button
                data-testid="complete-password-reset-button"
                type="submit"
                disabled={loading || !isStrongPassword(newPassword) || newPassword !== confirmPassword}
                className="rounded-none w-full bg-[#002FA7] text-white px-5 py-3 font-semibold hover:bg-[#002FA7]/90 inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <KeyRound className="w-4 h-4" /> {loading ? "Menyimpan…" : "Simpan Password Baru"}
              </button>
            </form>
          </>
        )}

        {error && (
          <div role="alert" className="mt-5 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </main>
    </div>
  );
}
